<script lang="ts" setup>
import { VueLenis } from 'lenis/vue'
import type { TransitionProps } from 'vue'

const globalState = useGlobalState()
const config = useRuntimeConfig().public

await callOnce('global-data', async () => {
  const { data: footerData } = useSanityQuery<FooterProps>(getFooter)
  globalState.footerData.value = footerData
})

const { $tweakpane, $gsap, $ScrollTrigger } = useNuxtApp()

const lenisOptions = computed(() => {
  return {
    lerp: $tweakpane.PARAMS.lenisLerp,
    wheelMultiplier: $tweakpane.PARAMS.lenisWheelMultiplier,
  }
})

const lenisRef = ref()

watchEffect((onInvalidate) => {
  if (import.meta.server) return

  function update(time: number) {
    // @ts-expect-error: fpsGraph is typed like a blade
    $tweakpane.fpsGraph?.begin()

    lenisRef.value?.lenis?.raf(time * 1000)
    $tweakpane.PARAMS.scroll = lenisRef.value?.lenis.scroll || 0
    $tweakpane.PARAMS.scrollProgress = lenisRef.value?.lenis.progress || 0
    $tweakpane.PARAMS.scrollDirection = lenisRef.value?.lenis.direction || 0

    // @ts-expect-error: fpsGraph is typed like a blade
    $tweakpane.fpsGraph?.end()
  }

  $gsap.ticker.add(update)

  onInvalidate(() => {
    $gsap.ticker.remove(update)
  })
})

useHead({
  meta: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0',
    },
    {
      name: 'theme-color',
      content: '#e6e6e6',
    },
  ],
  htmlAttrs: { lang: 'en' },
  link: [
    {
      rel: 'icon',
      type: 'image/svg',
      href: '/favicon-black.svg',
      media: '(prefers-color-scheme: light)',
    },
    {
      rel: 'icon',
      type: 'image/svg',
      href: '/favicon-white.svg',
      media: '(prefers-color-scheme: dark)',
    },
    {
      rel: 'preconnect',
      href: 'https://sanity.io',
    },
    {
      rel: 'preconnect',
      href: 'https://cdn.sanity.io',
    },
  ],
})

const oldScrollY = ref(0)

let overlay: HTMLElement | null = null

const transitions: Record<string, TransitionProps> = {
  default: {
    onLeave: async (el) => {
      const page = el as HTMLElement

      page.style.transformOrigin = `left ${window.innerHeight / 2 + lenisRef.value?.lenis.scroll}px`

      return new Promise(async (resolve) => {
        await page.animate([{ filter: 'brightness(0.7)', transform: 'translateX(-10vw)' }], {
          duration: 600,
          easing: 'cubic-bezier(0.83, 0, 0.17, 1)',
          fill: 'forwards',
        }).finished

        setTimeout(resolve, 500)
      })
    },
    onEnter: async (el) => {
      const page = el as HTMLElement

      page.style.position = 'relative'
      page.style.zIndex = '21'

      return new Promise(async (resolve) => {
        await page.animate([{ translate: '100% 0' }, { translate: '0 0' }], {
          duration: 1200,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        }).finished

        resolve()
      })
    },
  },
  infinite: {
    onLeave: async () => {
      return new Promise(async (resolve) => {
        setTimeout(() => {
          resolve()
        }, 2000)
      })
    },
    onBeforeEnter(el) {
      const page = el as HTMLElement

      page.style.clipPath = `inset(${window.innerHeight - 56}px 0 0 0)`
      page.style.position = 'relative'
      page.style.zIndex = '21'

      overlay = document.createElement('div')

      overlay.style.background = '#d7ff00'
      overlay.style.height = '100vh'
      overlay.style.width = '100%'
      overlay.style.position = 'fixed'
      overlay.style.top = '0'
      overlay.style.left = '0'
      overlay.style.zIndex = '1000'

      page.append(overlay)
    },
    onEnter: async (el) => {
      const page = el as HTMLElement

      const easing = 'cubic-bezier(0.83, 0, 0.17, 1)'

      await page.animate(
        { clipPath: 'inset(0 0 0 0)' },
        { duration: 800, fill: 'forwards', easing },
      ).finished

      const content = page.querySelector('.page-sections-wrapper') as HTMLElement

      if (content) {
        content.style.transformOrigin = `center ${window.innerHeight / 2}px`

        content.animate([{ transform: 'scale(1.3)' }, { transform: 'scale(1)' }], {
          duration: 2000,
          fill: 'forwards',
          easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
        })
      }

      if (overlay) {
        await overlay.animate(
          { transform: 'translateY(-100vh)' },
          { duration: 1200, fill: 'forwards', easing },
        ).finished
      }
    },
  },
}

const transitionRouter: TransitionProps = {
  mode: 'default',
  css: false,
  async onBeforeLeave(el) {
    const transition = globalState.loadedTransition.value || 'default'
    const hooks = transitions[transition]

    if (typeof hooks?.onBeforeLeave === 'function') {
      await hooks?.onBeforeLeave(el)
    }

    lenisRef.value?.lenis.stop()
    oldScrollY.value = lenisRef.value?.lenis.scroll

    globalState.isTransitioning.value = true

    const leavingPage = el as HTMLElement
    leavingPage.style.position = 'fixed'
    leavingPage.style.width = '100%'
    leavingPage.style.top = oldScrollY.value * -1 + 'px'
    leavingPage.style.zIndex = '20'
  },
  async onLeave(el, done) {
    const transition = globalState.loadedTransition.value || 'default'
    const hooks = transitions[transition]

    if (typeof hooks?.onLeave === 'function') {
      await hooks?.onLeave(el, done)
    }

    done()
  },
  async onBeforeEnter(el) {
    const transition = globalState.loadedTransition.value || 'default'
    const hooks = transitions[transition]

    if (typeof hooks?.onBeforeEnter === 'function') {
      await hooks?.onBeforeEnter(el)
    }
  },
  async onEnter(el, done) {
    const transition = globalState.loadedTransition.value || 'default'
    const hooks = transitions[transition]

    if (typeof hooks?.onEnter === 'function') {
      await hooks?.onEnter(el, done)
    }

    done()
  },
  onAfterEnter(el) {
    if ($ScrollTrigger) {
      $ScrollTrigger.refresh()
    }
    globalState.loadedTransition.value = 'default'
    globalState.isTransitioning.value = false

    el.removeAttribute('style')

    lenisRef.value?.lenis.start()
  },
}

if (import.meta.dev) {
  watch(
    globalState.isTransitioning,
    () => {
      $tweakpane.PARAMS.isTransitioning = globalState.isTransitioning.value
    },
    { immediate: true },
  )
}

const { width: innerWidth } = useWindowSize()
const debouncedSetup = useDebounceFn(() => {
  // $ScrollTrigger.getAll().forEach((t) => {
  //   console.log('All trigger::::', t)
  // })
  $ScrollTrigger?.refresh()
}, 200)

watch(innerWidth, debouncedSetup)
// watch(innerHeight, debouncedSetup)

const introDone = ref(false)

const { activeSection } = useActiveSection()

const themes = {
  yellow: {
    '--logo': 'var(--color-neon-yellow)',
    '--link-text': 'var(--color-black)',
    '--link-bg': 'var(--color-neon-yellow-60)',
    '--link-bg-progress': 'var(--color-neon-yellow)',
  },
  black: {
    '--logo': 'var(--color-black)',
    '--link-text': 'var(--color-neon-yellow)',
    '--link-bg': 'var(--color-black-50)',
    '--link-bg-progress': 'var(--color-black)',
  },
}

const route = useRoute()

const defaultTheme = computed(() => (route.name === 'page-slug' ? 'black' : 'yellow'))
</script>

<template>
  <main
    class="w-full overflow-x-clip"
    :style="themes[activeSection ? activeSection?.theme : defaultTheme]"
  >
    <PageLoadAnimation
      v-if="config.INTRO_ANIMATION_ENABLED === 'true' && !introDone"
      @done="introDone = true"
    />

    <SiteHeader class="" />

    <FrontpageNavigation />

    <VueLenis ref="lenisRef" :auto-raf="false" root :options="lenisOptions">
      <NuxtPage data-intro-target :transition="transitionRouter" />
    </VueLenis>
  </main>
</template>

<style lang="postcss">
.tp-dfwv {
  position: fixed !important;
  z-index: 100;
  top: 55px !important;
  right: var(--spacing-bleed) !important;

  user-select: none;

  * {
    user-select: none;
  }
}
</style>
